/**
 * FERP Portal - Error Trace Panel (Architect View)
 *
 * Real-time error monitoring and debugging interface.
 * Shows GraphQL validation errors, backend errors, and HTTP errors
 * with detailed context for investigation.
 */

import { Tabulator, type CellComponent } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Badge,
  Text,
  Title,
  Alert,
  Paper,
  SimpleGrid,
  Loader,
  Center,
  ThemeIcon,
  Divider,
  Modal,
  Code,
  ActionIcon,
  Menu,
  Button,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconAlertCircle,
  IconAlertOctagon,
  IconX,
  IconCheck,
  IconDownload,
  IconFileText,
} from '@tabler/icons-react';
import { useErrorTrace, type ErrorTrace } from '@/hooks/useErrorTrace';
import { TABULATOR_DEFAULTS } from '@/utils/tabulatorConfig';
import { globalErrorStore, type DetailedError } from '@/utils/globalErrorStore';
import '@/styles/tabulator-mantine.css';

const SEVERITY_CONFIG = {
  low: { color: 'blue', icon: IconCheck, label: 'Low' },
  medium: { color: 'yellow', icon: IconAlertCircle, label: 'Medium' },
  high: { color: 'orange', icon: IconAlertTriangle, label: 'High' },
  critical: { color: 'red', icon: IconAlertOctagon, label: 'Critical' },
} as const;

const CATEGORY_CONFIG = {
  validation: { color: 'yellow', label: 'Validation' },
  backend: { color: 'red', label: 'Backend' },
  http: { color: 'orange', label: 'HTTP' },
  unknown: { color: 'gray', label: 'Unknown' },
} as const;

/**
 * Generate text report from error summary WITH DETAILED ERRORS
 * Reusable utility for error reporting
 */
function generateErrorReport(
  summary: { totalErrors: number; errorRate: number; byCategory: Record<string, number>; bySeverity: Record<string, number>; allErrors: ErrorTrace[] },
  viewType: 'summary' | 'complete'
): string {
  const timestamp = new Date().toLocaleString();
  const lines: string[] = [];

  lines.push('='.repeat(80));
  lines.push('FERP ERROR MONITORING REPORT (WITH DETAILED CONTEXT)');
  lines.push('='.repeat(80));
  lines.push(`Generated: ${timestamp}`);
  lines.push(`Total Errors: ${summary.totalErrors}`);
  lines.push(`Error Rate: ${summary.errorRate.toFixed(2)}%`);
  lines.push('');

  lines.push('BREAKDOWN BY SEVERITY:');
  lines.push('-'.repeat(80));
  Object.entries(summary.bySeverity).forEach(([severity, count]) => {
    lines.push(`  ${severity.toUpperCase().padEnd(10)}: ${count}`);
  });
  lines.push('');

  lines.push('BREAKDOWN BY CATEGORY:');
  lines.push('-'.repeat(80));
  Object.entries(summary.byCategory).forEach(([category, count]) => {
    lines.push(`  ${category.toUpperCase().padEnd(12)}: ${count}`);
  });
  lines.push('');

  if (viewType === 'summary') {
    lines.push('ERROR SUMMARY (Unique Error Types with Detailed Examples):');
    lines.push('='.repeat(80));
    summary.allErrors.forEach((error, idx) => {
      lines.push(`${idx + 1}. ${error.code}`);
      lines.push(`   Severity: ${error.severity.toUpperCase()}`);
      lines.push(`   Category: ${error.category}`);
      lines.push(`   Count: ${error.count.toLocaleString()}`);
      lines.push(`   Rate: ${error.percentage.toFixed(2)}%`);
      lines.push(`   Generic Description: ${error.description}`);
      lines.push(`   Remediation: ${getRemediationGuidance(error)}`);
      lines.push('');

      // Fetch detailed errors from globalErrorStore
      const detailedErrors = globalErrorStore.getByCode(error.code);
      if (detailedErrors.length > 0) {
        lines.push(`   DETAILED ERROR EXAMPLES (showing ${Math.min(3, detailedErrors.length)} of ${detailedErrors.length}):`);
        lines.push('   ' + '-'.repeat(76));
        detailedErrors.slice(0, 3).forEach((detail, detailIdx) => {
          lines.push(`   Example ${detailIdx + 1}:`);
          lines.push(`     Time: ${detail.timestamp.toLocaleTimeString()}`);
          lines.push(`     Operation: ${detail.operationType} ${detail.operationName}`);
          lines.push(`     Message: ${detail.message}`);
          if (detail.path) {
            lines.push(`     Path: ${detail.path.join(' → ')}`);
          }
          if (detail.locations && detail.locations.length > 0) {
            // @ts-expect-error Possibly undefined
            lines.push(`     Location: Line ${detail.locations[0].line}, Column ${detail.locations[0].column}`);
          }
          if (detail.serviceName) {
            lines.push(`     Service: ${detail.serviceName}`);
          }
          if (detail.variableKeys && detail.variableKeys.length > 0) {
            lines.push(`     Variables: ${detail.variableKeys.join(', ')}`);
          }
          lines.push('');
        });
      } else {
        lines.push(`   (No detailed errors captured yet - check in-browser ErrorTracePanel)`);
        lines.push('');
      }
      lines.push('');
    });
  } else {
    lines.push('COMPLETE ERROR LIST (All Occurrences with Full Details):');
    lines.push('='.repeat(80));

    // Get all detailed errors from globalErrorStore
    const allDetailedErrors = globalErrorStore.getAll();

    if (allDetailedErrors.length > 0) {
      lines.push(`Found ${allDetailedErrors.length} detailed error captures`);
      lines.push('');

      allDetailedErrors.forEach((detail, idx) => {
        lines.push(`[${idx + 1}/${allDetailedErrors.length}] ${detail.code} - ${detail.severity.toUpperCase()}`);
        lines.push(`  Time: ${detail.timestamp.toLocaleString()}`);
        lines.push(`  Operation: ${detail.operationType} ${detail.operationName}`);
        lines.push(`  Message: ${detail.message}`);

        if (detail.path) {
          lines.push(`  Path: ${detail.path.join(' → ')}`);
        }

        if (detail.locations && detail.locations.length > 0) {
          // @ts-expect-error Possibly undefined
          lines.push(`  Location: Line ${detail.locations[0].line}, Column ${detail.locations[0].column}`);
        }

        if (detail.serviceName) {
          lines.push(`  Service: ${detail.serviceName}`);
        }

        if (detail.variableKeys && detail.variableKeys.length > 0) {
          lines.push(`  Variables: ${detail.variableKeys.join(', ')}`);
        }

        if (detail.stackTrace && detail.stackTrace.length > 0) {
          lines.push(`  Stack Trace (top 2):`);
          detail.stackTrace.slice(0, 2).forEach(frame => {
            lines.push(`    ${frame}`);
          });
        }

        lines.push('');
      });
    } else {
      // Fallback to Prometheus data if no detailed errors
      lines.push('(Using Prometheus aggregate data - no detailed captures available)');
      lines.push('');
      summary.allErrors.forEach((error) => {
        for (let i = 0; i < error.count; i++) {
          lines.push(`[${error.severity.toUpperCase()}] ${error.code}`);
          lines.push(`  Category: ${error.category} | Rate: ${error.percentage.toFixed(2)}%`);
          lines.push(`  ${error.description}`);
          lines.push('');
        }
      });
    }
  }

  lines.push('='.repeat(80));
  lines.push('END OF DETAILED REPORT');
  lines.push('='.repeat(80));

  return lines.join('\n');
}

/**
 * Download text file utility (reusable)
 */
function downloadTextFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ErrorTracePanel(): React.ReactElement {
  const { summary, loading, error } = useErrorTrace(10000); // Refresh every 10s
  const tableRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<any | null>(null);
  const [selectedError, setSelectedError] = useState<ErrorTrace | null>(null);
  const [detailedErrors, setDetailedErrors] = useState<DetailedError[]>([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [inMemoryErrorCount, setInMemoryErrorCount] = useState(0);

  // Callback refs for stable access in Tabulator
  const handleRowClick = useRef((errorData: ErrorTrace) => {
    console.log('Row clicked:', errorData);
    setSelectedError(errorData);

    // Fetch detailed errors for this error code
    const detailed = globalErrorStore.getByCode(errorData.code);
    console.log(`Found ${detailed.length} detailed errors for code ${errorData.code}`);
    setDetailedErrors(detailed);

    setDetailModalOpen(true);
  });

  // Update callback ref
  useEffect(() => {
    handleRowClick.current = (errorData: ErrorTrace) => {
      console.log('Row clicked:', errorData);
      setSelectedError(errorData);

      // Fetch detailed errors for this error code
      const detailed = globalErrorStore.getByCode(errorData.code);
      console.log(`Found ${detailed.length} detailed errors for code ${errorData.code}`);
      setDetailedErrors(detailed);

      setDetailModalOpen(true);
    };
  }, []);

  // Poll in-memory error count every 5 seconds
  useEffect(() => {
    const updateCount = () => {
      const count = globalErrorStore.getAll().length;
      setInMemoryErrorCount(count);
    };

    // Initial update
    updateCount();

    // Poll every 5 seconds
    const interval = setInterval(updateCount, 5000);

    return () => clearInterval(interval);
  }, []);

  // Download handlers
  const handleDownloadSummary = useCallback(() => {
    if (!summary) return;
    const report = generateErrorReport(summary, 'summary');
    const timestamp = new Date().toISOString().split('T')[0];
    downloadTextFile(report, `ferp_error_summary_${timestamp}.txt`);
  }, [summary]);

  const handleDownloadComplete = useCallback(() => {
    if (!summary) return;
    const report = generateErrorReport(summary, 'complete');
    const timestamp = new Date().toISOString().split('T')[0];
    downloadTextFile(report, `ferp_error_complete_${timestamp}.txt`);
  }, [summary]);

  const handleDownloadTable = useCallback((format: 'csv' | 'json' | 'xlsx') => {
    if (!tabulatorRef.current) return;
    const timestamp = new Date().toISOString().split('T')[0];
    tabulatorRef.current.download(format, `ferp_errors_${timestamp}.${format}`);
  }, []);

  // Initialize Tabulator when data is available
  useEffect(() => {
    if (!tableRef.current || !summary?.allErrors) return;

    // Destroy existing instance
    if (tabulatorRef.current) {
      tabulatorRef.current.destroy();
    }

    console.log('✅ Creating Tabulator with', summary.allErrors.length, 'unique error types');

    // Create new Tabulator instance
    const table = new Tabulator(tableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: summary.allErrors,
      layout: 'fitDataTable',
      height: '500px',
      initialSort: [{ column: 'count', dir: 'desc' }],
      selectable: false,
      reactiveData: true,
      columns: [
        {
          title: 'Severity',
          field: 'severity',
          width: 120,
          sorter: 'string',
          headerFilter: 'list',
          headerFilterParams: {
            values: { '': 'All', critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' },
          },
          formatter: (cell: CellComponent) => {
            const severity = cell.getValue() as string;
            const config = SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG];
            return `<span class="ferp-tbl-badge ferp-tbl-badge--${config.color}">${config.label}</span>`;
          },
          cellClick: (_e: unknown, cell: CellComponent) => {
            const row = cell.getRow();
            // @ts-expect-error Type conversion
            const errorData = row.getData() as ErrorTrace;
            console.log('🖱️ Cell clicked, triggering modal for:', errorData.code);
            handleRowClick.current(errorData);
          },
        },
        {
          title: 'Code',
          field: 'code',
          width: 250,
          sorter: 'string',
          headerFilter: 'input',
          formatter: (cell: CellComponent) => {
            const code = cell.getValue() as string;
            return `<code style="font-size: 0.875rem; font-weight: 600; cursor: pointer;">${code}</code>`;
          },
          cellClick: (_e: unknown, cell: CellComponent) => {
            const row = cell.getRow();
            // @ts-expect-error Type conversion
            handleRowClick.current(row.getData() as ErrorTrace);
          },
        },
        {
          title: 'Category',
          field: 'category',
          width: 140,
          sorter: 'string',
          headerFilter: 'list',
          headerFilterParams: {
            values: { '': 'All', validation: 'Validation', backend: 'Backend', http: 'HTTP', unknown: 'Unknown' },
          },
          formatter: (cell: CellComponent) => {
            const category = cell.getValue() as string;
            const config = CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG];
            return `<span class="ferp-tbl-badge ferp-tbl-badge--${config.color}">${config.label}</span>`;
          },
          cellClick: (_e: unknown, cell: CellComponent) => {
            const row = cell.getRow();
            // @ts-expect-error Type conversion
            handleRowClick.current(row.getData() as ErrorTrace);
          },
        },
        {
          title: 'Description',
          field: 'description',
          sorter: 'string',
          headerFilter: 'input',
          cellClick: (_e: unknown, cell: CellComponent) => {
            const row = cell.getRow();
            // @ts-expect-error Type conversion
            handleRowClick.current(row.getData() as ErrorTrace);
          },
        },
        {
          title: 'Count',
          field: 'count',
          width: 100,
          sorter: 'number',
          hozAlign: 'right',
          formatter: (cell: CellComponent) => {
            const count = cell.getValue() as number;
            return `<strong style="cursor: pointer;">${count.toLocaleString()}</strong>`;
          },
          cellClick: (_e: unknown, cell: CellComponent) => {
            const row = cell.getRow();
            // @ts-expect-error Type conversion
            handleRowClick.current(row.getData() as ErrorTrace);
          },
        },
        {
          title: 'Rate',
          field: 'percentage',
          width: 100,
          sorter: 'number',
          hozAlign: 'right',
          formatter: (cell: CellComponent) => {
            const percentage = cell.getValue() as number;
            const color = percentage > 5 ? 'red' : percentage > 1 ? 'orange' : 'inherit';
            return `<span style="color: var(--mantine-color-${color}-6); font-weight: 600; cursor: pointer;">${percentage.toFixed(2)}%</span>`;
          },
          cellClick: (_e: unknown, cell: CellComponent) => {
            const row = cell.getRow();
            // @ts-expect-error Type conversion
            handleRowClick.current(row.getData() as ErrorTrace);
          },
        },
      ],
    });

    // Add CSS for row hover
    table.on('rowMouseEnter', (_: any, row: { getElement: () => HTMLElement }) => {
      row.getElement().style.backgroundColor = 'var(--mantine-color-gray-1)';
      row.getElement().style.cursor = 'pointer';
    });

    table.on('rowMouseLeave', (_: any, row: { getElement: () => HTMLElement }) => {
      row.getElement().style.backgroundColor = '';
    });

    tabulatorRef.current = table;
    console.log('✅ Tabulator initialized successfully');

    return () => {
      if (tabulatorRef.current) {
        tabulatorRef.current.destroy();
        tabulatorRef.current = null;
      }
    };
  }, [summary?.allErrors]);

  if (loading && !summary) {
    return (
      <Center p="xl">
        <Stack ta="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading error traces...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error Trace Unavailable" icon={<IconX />}>
        Unable to fetch error metrics: {error.message}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert color="gray" title="No Data">
        Error tracing data is not available.
      </Alert>
    );
  }

  const hasCriticalErrors = (summary.bySeverity.critical || 0) > 0;
  const hasErrors = summary.totalErrors > 0;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={3}>Error Trace Monitor</Title>
          <Text size="sm" c="dimmed">
            Real-time GraphQL and backend error tracking
          </Text>
        </div>
        {hasCriticalErrors && (
          <Badge size="lg" color="red" leftSection={<IconAlertOctagon size={14} />}>
            CRITICAL ERRORS DETECTED
          </Badge>
        )}
      </Group>

      {/* Summary Stats */}
      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
            Total Errors
          </Text>
          <Group gap="xs" ta="left">
            <Text size="xl" fw={700} c={hasErrors ? 'red' : 'green'}>
              {summary.totalErrors}
            </Text>
            <Badge size="sm" color={summary.errorRate > 5 ? 'red' : summary.errorRate > 1 ? 'yellow' : 'green'}>
              {summary.errorRate.toFixed(2)}% rate
            </Badge>
          </Group>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
            By Severity
          </Text>
          <Stack gap={4}>
            {Object.entries(summary.bySeverity).map(([severity, count]) => (
              <Group key={severity} justify="space-between" gap="xs">
                <Badge size="xs" color={SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG].color}>
                  {severity}
                </Badge>
                <Text size="sm" fw={500}>
                  {count}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
            By Category
          </Text>
          <Stack gap={4}>
            {Object.entries(summary.byCategory).map(([category, count]) => (
              <Group key={category} justify="space-between" gap="xs">
                <Badge size="xs" color={CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG].color}>
                  {CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG].label}
                </Badge>
                <Text size="sm" fw={500}>
                  {count}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
            Top Error
          </Text>
          {summary.topErrors.length > 0 ? (
            <>
              <Text size="sm" fw={600} mb={4}>
                {summary.topErrors[0]?.code}
              </Text>
              <Text size="xs" c="dimmed">
                {summary.topErrors[0]?.count} occurrences
              </Text>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              No errors
            </Text>
          )}
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
            Detailed Captures
          </Text>
          <Group gap="xs" ta="left">
            <Text size="xl" fw={700} c={inMemoryErrorCount > 0 ? 'green' : 'gray'}>
              {inMemoryErrorCount}
            </Text>
            <Badge size="sm" color={inMemoryErrorCount > 0 ? 'green' : 'gray'}>
              {inMemoryErrorCount > 0 ? 'Active' : 'Empty'}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            In-memory error details
          </Text>
        </Paper>
      </SimpleGrid>

      {/* All Errors Table */}
      {summary.allErrors.length > 0 && (
        <Card withBorder p="lg" radius="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>
              All Errors ({summary.allErrors.length} unique types, {summary.totalErrors} total occurrences)
            </Title>
            <Group gap="xs">
              <Menu shadow="md" width={220}>
                <Menu.Target>
                  <ActionIcon variant="light" size="lg" title="Download Table Data">
                    <IconDownload size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Table Export</Menu.Label>
                  <Menu.Item onClick={() => handleDownloadTable('csv')}>
                    Download CSV
                  </Menu.Item>
                  <Menu.Item onClick={() => handleDownloadTable('json')}>
                    Download JSON
                  </Menu.Item>
                  <Menu.Item onClick={() => handleDownloadTable('xlsx')}>
                    Download Excel
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Badge
                size="lg"
                color={inMemoryErrorCount > 0 ? 'green' : 'gray'}
                variant="light"
                title="Detailed errors captured in this browser session"
              >
                {inMemoryErrorCount} detailed {inMemoryErrorCount === 1 ? 'capture' : 'captures'}
              </Badge>

              <Menu shadow="md" width={240}>
                <Menu.Target>
                  <Button
                    leftSection={<IconFileText size={16} />}
                    variant="light"
                    size="sm"
                  >
                    Export Report
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Report Type</Menu.Label>
                  <Menu.Item
                    onClick={handleDownloadSummary}
                    leftSection={<IconFileText size={14} />}
                  >
                    Summary Report
                    <Text size="xs" c="dimmed">
                      {summary.allErrors.length} error types with examples
                    </Text>
                  </Menu.Item>
                  <Menu.Item
                    onClick={handleDownloadComplete}
                    leftSection={<IconFileText size={14} />}
                  >
                    Complete Report
                    <Text size="xs" c="dimmed">
                      {inMemoryErrorCount > 0
                        ? `${inMemoryErrorCount} detailed captures`
                        : 'Prometheus data only'}
                    </Text>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
          <div ref={tableRef} />
        </Card>
      )}

      {/* Remediation Guidance */}
      {summary.topErrors.length > 0 && (
        <Alert color="blue" title="Debugging Guidance" icon={<IconAlertCircle />}>
          <Stack gap="xs">
            {summary.topErrors.slice(0, 3).map((trace) => (
              <div key={trace.code}>
                <Text size="sm" fw={600}>
                  {trace.code}:
                </Text>
                <Text size="sm">{getRemediationGuidance(trace)}</Text>
                <Divider my="xs" />
              </div>
            ))}
          </Stack>
        </Alert>
      )}

      {/* No Errors State */}
      {summary.totalErrors === 0 && (
        <Alert color="green" title="All Systems Healthy" icon={<IconCheck />}>
          No errors detected in the current monitoring window.
        </Alert>
      )}

      {/* Error Detail Modal */}
      <Modal
        opened={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={
          selectedError && (
            <Group>
              <ThemeIcon
                size="lg"
                color={SEVERITY_CONFIG[selectedError.severity].color}
                variant="light"
              >
                {React.createElement(SEVERITY_CONFIG[selectedError.severity].icon, { size: 20 })}
              </ThemeIcon>
              <div>
                <Text fw={600}>Error Details</Text>
                <Text size="xs" c="dimmed">
                  <Code>{selectedError.code}</Code>
                </Text>
              </div>
            </Group>
          )
        }
        size="lg"
      >
        {selectedError && (
          <Stack gap="md">
            {/* Error Metadata */}
            <SimpleGrid cols={2}>
              <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                  Severity
                </Text>
                <Badge
                  size="lg"
                  color={SEVERITY_CONFIG[selectedError.severity].color}
                  leftSection={React.createElement(SEVERITY_CONFIG[selectedError.severity].icon, { size: 14 })}
                >
                  {SEVERITY_CONFIG[selectedError.severity].label}
                </Badge>
              </Paper>

              <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                  Category
                </Text>
                <Badge size="lg" color={CATEGORY_CONFIG[selectedError.category].color}>
                  {CATEGORY_CONFIG[selectedError.category].label}
                </Badge>
              </Paper>

              <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                  Occurrence Count
                </Text>
                <Text size="xl" fw={700}>
                  {selectedError.count.toLocaleString()}
                </Text>
              </Paper>

              <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                  Error Rate
                </Text>
                <Text
                  size="xl"
                  fw={700}
                  c={selectedError.percentage > 5 ? 'red' : selectedError.percentage > 1 ? 'orange' : 'green'}
                >
                  {selectedError.percentage.toFixed(2)}%
                </Text>
              </Paper>
            </SimpleGrid>

            <Divider />

            {/* Description */}
            <div>
              <Text size="sm" fw={600} mb="xs">
                Description
              </Text>
              <Text size="sm" c="dimmed">
                {selectedError.description}
              </Text>
            </div>

            <Divider />

            {/* Remediation */}
            <Alert color="blue" title="Remediation Steps" icon={<IconAlertCircle />}>
              <Text size="sm">{getRemediationGuidance(selectedError)}</Text>
            </Alert>

            {/* Detailed Errors (from globalErrorStore) */}
            {detailedErrors.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text size="sm" fw={600} mb="md">
                    Recent Error Details ({detailedErrors.length} captured)
                  </Text>
                  <Stack gap="md">
                    {detailedErrors.slice(0, 5).map((detailedErr, idx) => (
                      <Paper key={detailedErr.id} withBorder p="md" radius="md" bg="var(--mantine-color-gray-0)">
                        <Group justify="space-between" mb="xs">
                          <Text size="xs" c="dimmed">
                            #{idx + 1} - {detailedErr.timestamp.toLocaleTimeString()}
                          </Text>
                          <Badge size="xs" color={SEVERITY_CONFIG[detailedErr.severity].color}>
                            {detailedErr.operationType}
                          </Badge>
                        </Group>

                        <Stack gap="xs">
                          <div>
                            <Text size="xs" c="dimmed">Operation:</Text>
                            <Code>{detailedErr.operationName}</Code>
                          </div>

                          <div>
                            <Text size="xs" c="dimmed">Message:</Text>
                            <Text size="sm" fw={500}>{detailedErr.message}</Text>
                          </div>

                          {detailedErr.path && (
                            <div>
                              <Text size="xs" c="dimmed">Path:</Text>
                              <Code>{detailedErr.path.join(' → ')}</Code>
                            </div>
                          )}

                          {detailedErr.locations && detailedErr.locations.length > 0 && (
                            <div>
                              <Text size="xs" c="dimmed">Location:</Text>
                              <Code>Line {detailedErr.locations[0]?.line}, Column {detailedErr.locations[0]?.column}</Code>
                            </div>
                          )}

                          {detailedErr.serviceName && (
                            <div>
                              <Text size="xs" c="dimmed">Service:</Text>
                              <Badge size="sm" variant="light">{detailedErr.serviceName}</Badge>
                            </div>
                          )}

                          {detailedErr.variableKeys && detailedErr.variableKeys.length > 0 && (
                            <div>
                              <Text size="xs" c="dimmed">Variables:</Text>
                              <Code>{detailedErr.variableKeys.join(', ')}</Code>
                            </div>
                          )}

                          {detailedErr.stackTrace && detailedErr.stackTrace.length > 0 && (
                            <div>
                              <Text size="xs" c="dimmed">Stack Trace (top 2):</Text>
                              <Code block style={{ fontSize: '10px', lineHeight: '1.3' }}>
                                {detailedErr.stackTrace.slice(0, 2).join('\n')}
                              </Code>
                            </div>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                    {detailedErrors.length > 5 && (
                      <Text size="xs" c="dimmed" ta="center">
                        ... and {detailedErrors.length - 5} more occurrences
                      </Text>
                    )}
                  </Stack>
                </div>
              </>
            )}

            {/* Additional Context */}
            <Paper withBorder p="md" radius="md" bg="var(--mantine-color-gray-0)">
              <Text size="xs" c="dimmed" mb="xs">
                Error Code
              </Text>
              <Code block>{selectedError.code}</Code>

              <Text size="xs" c="dimmed" mt="md" mb="xs">
                First Detected
              </Text>
              <Text size="sm">{selectedError.timestamp.toLocaleString()}</Text>
            </Paper>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

/**
 * Provide remediation guidance for specific error codes
 */
function getRemediationGuidance(trace: ErrorTrace): string {
  const guidance: Record<string, string> = {
    'GRAPHQL_VALIDATION_FAILED':
      'Frontend is sending queries with invalid schema. Check `generated/graphql.ts` to ensure types match backend schema. Run `pnpm codegen` to regenerate types.',
    'FERP-9001':
      'Backend service error - check gateway logs with `docker logs ferp-gateway`. Common causes: missing DynamoDB fields, repository errors, or null pointer issues.',
    'FERP-5002':
      'Database repository error - check DynamoDB table schema and ensure all required fields exist. May indicate data migration needed.',
    'FERP-1001':
      'Entity not found - verify entity IDs are correct and entities exist in database.',
    'VALIDATION_INVALID_TYPE_VARIABLE':
      'GraphQL variable type mismatch - check query variables match schema types.',
  };

  return guidance[trace.code] || `Check backend logs for error code ${trace.code}`;
}

export default ErrorTracePanel;
